import { useState, useEffect, useCallback, useRef } from 'react';
import Video, { Room, Participant as TwilioParticipant } from 'twilio-video';
import { toast } from 'sonner';

export type ConnectionErrorKind =
  | 'permission-denied'
  | 'missing-device'
  | 'device-in-use'
  | 'unsupported'
  | 'unknown';

interface UseTwilioRoomOptions {
  token: string | undefined;
  roomName: string | undefined;
}

interface UseTwilioRoomReturn {
  room: Room | undefined;
  localParticipant: TwilioParticipant | undefined;
  remoteParticipants: TwilioParticipant[];
  isConnecting: boolean;
  connectionError: ConnectionErrorKind | undefined;
  retry: () => void;
  disconnect: () => void;
}

const classifyConnectError = (error: unknown): ConnectionErrorKind => {
  const name = (error as { name?: string })?.name;
  if (name === 'NotAllowedError') {
    return 'permission-denied';
  }
  if (name === 'NotFoundError') {
    return 'missing-device';
  }
  if (name === 'NotReadableError' || name === 'AbortError') {
    return 'device-in-use';
  }
  if (name === 'NotSupportedError') {
    return 'unsupported';
  }
  return 'unknown';
};

const stopLocalTracks = (room: Room): void => {
  if (room.localParticipant?.state !== 'connected') {
    return;
  }
  room.localParticipant.tracks.forEach((publication) => {
    const track = publication.track;
    if (track && track.kind !== 'data') {
      track.stop();
      publication.unpublish();
    }
  });
};

const teardownRoom = (room: Room | undefined): void => {
  if (!room) {
    return;
  }
  stopLocalTracks(room);
  room.disconnect();
};

export const useTwilioRoom = ({ token, roomName }: UseTwilioRoomOptions): UseTwilioRoomReturn => {
  const roomRef = useRef<Room | undefined>(undefined);
  const cancelledRef = useRef(false);
  const [isConnected, setIsConnected] = useState(false);
  const [remoteParticipants, setRemoteParticipants] = useState<TwilioParticipant[]>([]);
  const [isConnecting, setIsConnecting] = useState(false);
  const [connectionError, setConnectionError] = useState<ConnectionErrorKind | undefined>(
    undefined
  );
  const [attempt, setAttempt] = useState(0);

  const retry = useCallback(() => {
    setConnectionError(undefined);
    setAttempt((n) => n + 1);
  }, []);

  const disconnect = useCallback(() => {
    cancelledRef.current = true;
    teardownRoom(roomRef.current);
    roomRef.current = undefined;
    setIsConnected(false);
    setIsConnecting(false);
    setRemoteParticipants([]);
  }, []);

  useEffect(() => {
    if (!token || !roomName) return;

    cancelledRef.current = false;
    setIsConnecting(true);
    setConnectionError(undefined);

    const participantConnected = (participant: TwilioParticipant) => {
      setRemoteParticipants((prev) => [...prev, participant]);
    };

    const participantDisconnected = (participant: TwilioParticipant) => {
      setRemoteParticipants((prev) => prev.filter((p) => p !== participant));
    };

    const roomDisconnected = (disconnectedRoom: Room, error?: { code?: number } | null) => {
      if (cancelledRef.current) return;
      stopLocalTracks(disconnectedRoom);
      roomRef.current = undefined;
      setIsConnected(false);
      setRemoteParticipants([]);

      if (error?.code === 53205) {
        toast.error(
          'This call was opened in another tab or device. Only one active session is allowed.'
        );
      }
    };

    Video.connect(token, { name: roomName })
      .then((videoRoom) => {
        if (cancelledRef.current) {
          teardownRoom(videoRoom);
          return;
        }
        roomRef.current = videoRoom;
        setIsConnected(true);
        setIsConnecting(false);

        videoRoom.on('participantConnected', participantConnected);
        videoRoom.on('participantDisconnected', participantDisconnected);
        videoRoom.on('disconnected', roomDisconnected);
        videoRoom.participants.forEach(participantConnected);
      })
      .catch((error: unknown) => {
        if (cancelledRef.current) return;
        setIsConnecting(false);
        setConnectionError(classifyConnectError(error));
      });

    return () => {
      cancelledRef.current = true;
      teardownRoom(roomRef.current);
      roomRef.current = undefined;
      setIsConnected(false);
      setRemoteParticipants([]);
    };
  }, [token, roomName, attempt]);

  return {
    room: isConnected ? roomRef.current : undefined,
    localParticipant: isConnected ? roomRef.current?.localParticipant : undefined,
    remoteParticipants,
    isConnecting,
    connectionError,
    retry,
    disconnect,
  };
};
